import { DateRange } from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment';
import { useDialog } from 'muibox';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { OnlyFansSubscriberMessageUsersAutoMessageDetails } from '../types/messageUsersAutoSendWelcomeMessage';
import { OnlyFansLists } from '../types/onlyFansTypes';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import { OnlyFansSubscriber } from './useSubscriber';

export interface OnlyFansMetricsSettings {
    platform?: string;
    goalSpent?: object | number;
    goalSpentNotificationSent?: boolean;
    goalSubs?: number;
    goalSubsNotificationSent?: boolean;
    addUsersToList?: { id: number | OnlyFansLists.Type; name: string } | null;
}

export type OnlyFansMetricType =
    | 'trackingLink'
    | 'promoCampaign'
    | 'trialLinkPromo'
    | 'trialLinkTrial'
    | 'countersAll'
    | 'earningsAll'
    | 'performerTop'
    | 'statisticsFansSubscriptionsOverview'
    | 'statisticsProfileVisitorsOverview';

export interface OnlyFansMetricsRoi {
    // totalEarnings: number;
    // totalSubscribers: number;
    amount: number | object | null;
    percentage: number | null;
}

export interface OnlyFansMetricsWelcomeMessage {
    _id?: string;
    subscriberId: string;
    metricId: string;
    messageDetails: OnlyFansSubscriberMessageUsersAutoMessageDetails;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface OnlyFansMetrics {
    _id: string;
    subscriberId: string;
    type: OnlyFansMetricType;
    foreignId?: number;
    associatedUsername?: string;
    associatedSubscriberId?: string;
    settings?: OnlyFansMetricsSettings;
    reachedEndOfUsersList?: boolean;
    reachedEndOfUsersListCount?: number;
    notes?: string;
    payload?: any;
    roi?: OnlyFansMetricsRoi;
    welcomeMessage?: OnlyFansMetricsWelcomeMessage;
}

export interface OnlyFansMetricsWithSubscriber extends OnlyFansMetrics {
    subscriber: {
        _id: string;
        username: string;
    };
}

export interface OnlyFansMetricsCampaignsPaginated {
    data: OnlyFansMetrics[];
    metadata: { total: number; earningsTotal: number | object; subscribersTotal: number; clicksTotal: number };
}

interface CampaignPayload {
    active?: boolean;
    completed?: boolean;
    createdAt?: Date;
    earningsTotal?: number;
    counters?: CountersPayload | null;
    url?: string;
    name?: string;
    cpf?: number;
}

interface BaseCampaign {
    _id: string;
    subscriberId: string;
    type: OnlyFansMetricType;
    payload?: CampaignPayload;
    settings?: OnlyFansMetricsSettings;
    recentEarnings?: number | object;
    associatedUsername?: string;
    foreignId?: string;
}

interface CountersPayload {
    [key: string]: any; // Define specific structure based on your data
}

interface ROI {
    amount: number;
    percentage: number;
}

export interface MetricCrossSubscriberEarningsResult {
    _id: string;
    total: number | object;
    totalNet: number | object;
    totalBefore: number | object;
    totalBeforeNet: number | object;
    totalWithoutSubscription: number | object;
    totalNetWithoutSubscription: number | object;
    totalBeforeWithoutSubscription: number | object;
    totalBeforeNetWithoutSubscription: number | object;
}

export interface EnrichedCampaign extends BaseCampaign {
    counters: CountersPayload | null;
    earningsTotal: number;
    subscribedToday: number;
    roi: ROI;
    crossReferenceEarnings?: MetricCrossSubscriberEarningsResult;
}

const useSextforceMetricsCampaignsOverview = (
    subscriber: OnlyFansSubscriber,
    timezone: string,
    dateRange: DateRange<Moment>,
    search: string,
    page: number,
    pageSize: number,
    sort: string,
    includeInactive: boolean,
    completed: boolean | null,
) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const dialog = useDialog();
    const axios = useAxios();

    // Fetch Paginated Report
    const fetchCampaignsOverview = async (): Promise<OnlyFansMetricsCampaignsPaginated> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
            const query: string = `${settingsContext.routes.metrics.base}${params.userId}/campaignsOverviewPaginated?${new URLSearchParams({
                includeInactive: includeInactive ? 'true' : 'false',
                ...(dateRange && dateRange[0] && dateRange[1]
                    ? {
                          dateRangeStartDate: moment(dateRange[0]).tz(timezone, true).startOf('day').format(),
                          dateRangeEndDate: moment(dateRange[1]).tz(timezone, true).endOf('day').format(),
                      }
                    : {}),
                search,
                page: page.toString(),
                pageSize: pageSize.toString(),
                sort,
                ...(completed ? { completed: completed.toString() } : {}),
            })}`;

            const data = await axios
                .get(query)
                .then(response => response.data)
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);

                    return { data: [], metadata: { total: 0, earningsTotal: 0, subscribersTotal: 0 } };
                });

            return data;
        }

        return { data: [], metadata: { total: 0, earningsTotal: 0, subscribersTotal: 0, clicksTotal: 0 } };
    };

    return useQuery(
        ['metricsCampaignsOverviewPaginated', timezone, includeInactive, subscriber, search, dateRange, page, pageSize, sort, completed],
        () => fetchCampaignsOverview(),
        {
            refetchOnWindowFocus: false,
            staleTime: 60 * 60 * 1000,
            enabled: subscriber ? true : false,
        },
    );
};

export default useSextforceMetricsCampaignsOverview;
